import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Check, Play, ShieldCheck, X } from '@phosphor-icons/react';
import { drumLanguageAtom } from '../../../shared/store/drumAppAtoms';
import { useDrumMidi } from '../../../shared/store/MidiContext';
import { createCognitiveReport, type CognitiveReport } from '../../../shared/data/cognitiveEngine';
import { readFeedbackHistory, readPreviousReport, saveCognitiveReport } from '../../../shared/data/cognitiveReportsStorage';
import {
  DRUMS, EXECUTIVE_CONFIG, EXECUTIVE_PROFILES, buildRuleSequence, playCognitiveAnswerTone,
  MidiStatus, type ExecutiveGameType, type ExecutiveProfile, type RuleTrial,
} from './cognitiveGameShared';
import CognitiveAnswerEffect, { type AnswerEffectResult } from './CognitiveAnswerEffect';
import CognitiveReportView from './CognitiveReportView';

interface StroopTrial {
  ink: typeof DRUMS[number];
  word: typeof DRUMS[number];
  expected: typeof DRUMS[number];
}

interface CompletedTrial {
  completed: true;
  sequence: string[];
  correct: boolean;
  errorType: 'sequence' | 'timeout' | 'perseverative' | null;
  responseTimes: number[];
  inputMode: 'touch' | 'midi';
  rule: string | null;
  isSwitch: boolean;
}

function ExecutiveColorGame({ type }: { type: ExecutiveGameType }) {
  const language = useAtomValue(drumLanguageAtom);
  const midi = useDrumMidi();
  const navigate = useNavigate();
  const onExit = () => navigate('/drum/cognitive');
  const isKorean = language === 'ko';
  const config = EXECUTIVE_CONFIG[type];
  const title = isKorean ? config.titleKo : config.titleEn;
  const subtitle = isKorean ? config.subtitleKo : config.subtitleEn;
  const domain = isKorean ? config.domainKo : config.domainEn;

  const [profile, setProfile] = useState<ExecutiveProfile>(EXECUTIVE_PROFILES[0]);
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [trials, setTrials] = useState<CompletedTrial[]>([]);
  const [report, setReport] = useState<CognitiveReport | null>(null);
  const [feedback, setFeedback] = useState('');
  const [answerEffect, setAnswerEffect] = useState<AnswerEffectResult>(null);
  const startedAt = useRef(0);
  const handled = useRef<number | null>(null);
  const locked = useRef(false);

  const item: StroopTrial | RuleTrial = useMemo(() => {
    if (type === 'stroop') {
      const list = DRUMS.slice(0, profile.colors);
      const ink = list[index % list.length];
      const word = list[(index + 1 + (index % 2)) % list.length];
      return { ink, word, expected: ink };
    }
    return buildRuleSequence(profile, index);
  }, [type, profile, index]);

  const begin = () => {
    locked.current = false;
    handled.current = midi.lastHit?.at || null;
    setIndex(0);
    setTrials([]);
    setReport(null);
    setFeedback('');
    setAnswerEffect(null);
    setStarted(true);
    startedAt.current = Date.now();
  };

  const complete = (correct: boolean, errorType: 'sequence' | 'timeout' | 'perseverative' | null = null, source: 'touch' | 'midi' = 'touch') => {
    if (locked.current) return;
    locked.current = true;
    const effect: AnswerEffectResult = correct ? 'correct' : 'wrong';
    setAnswerEffect(effect);
    playCognitiveAnswerTone(effect);
    const elapsed = Date.now() - startedAt.current;
    const sequence = type === 'stroop'
      ? [(item as StroopTrial).expected.id]
      : [...(item as RuleTrial).values, (item as RuleTrial).expected].map((drum) => drum.id);
    const row: CompletedTrial = {
      completed: true,
      sequence,
      correct,
      errorType,
      responseTimes: [elapsed],
      inputMode: source,
      rule: type === 'rule' ? (item as RuleTrial).rule : null,
      isSwitch: type === 'rule' && index === 3,
    };
    const next = [...trials, row];
    setTrials(next);
    setFeedback('');
    window.setTimeout(() => {
      if (next.length >= profile.trials) {
        const result = createCognitiveReport({
          activityId: config.id,
          activityVersion: '2.1.0',
          ageProfile: { id: profile.id, answerLimit: profile.limit, speedReference: profile.ref },
          inputMode: source,
          trials: next,
          previousReport: readPreviousReport(config.id, profile.id),
          feedbackHistory: readFeedbackHistory(),
          language,
        });
        saveCognitiveReport(result);
        setReport(result);
        return;
      }
      setIndex((value) => value + 1);
      startedAt.current = Date.now();
      setFeedback('');
      setAnswerEffect(null);
      locked.current = false;
    }, correct ? 720 : 880);
  };

  const choose = (id: string, source: 'touch' | 'midi' = 'touch') => {
    if (report || locked.current) return;
    if (id === item.expected.id) complete(true, null, source);
    else complete(false, type === 'rule' ? 'perseverative' : 'sequence', source);
  };

  useEffect(() => {
    if (started && !report && !locked.current) startedAt.current = Date.now();
  }, [started, report, index]);

  useEffect(() => {
    if (!started || report) return undefined;
    const timer = window.setTimeout(() => complete(false, 'timeout'), profile.limit * 1000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, report, index, profile.limit]);

  useEffect(() => {
    if (!started || report || !midi.lastHit || handled.current === midi.lastHit.at) return;
    handled.current = midi.lastHit.at;
    choose(midi.lastHit.drumId, 'midi');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midi.lastHit, started, report, index]);

  if (!started) {
    return (
      <main className="cognition-game cognition-level-select" data-localized-root>
        <header className="cognition-game-header">
          <div><span><small>{isKorean ? '인지 학습' : 'COGNITIVE LEARNING'}</small><strong>{title}</strong></span></div>
          <MidiStatus language={language} status={midi.status} deviceName={midi.deviceName} onScan={midi.scan} />
          <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{isKorean ? '종료' : 'EXIT'}</button>
        </header>
        <section className="cognition-launch-v2">
          <div className="cognition-level-intro">
            <span>{domain}</span>
            <h1>{isKorean ? '학습 기준을 선택하세요' : 'Choose a learning reference'}</h1>
            <p>{subtitle}</p>
          </div>
          <div className="age-profile-grid">
            {EXECUTIVE_PROFILES.map((item2) => (
              <button type="button" key={item2.id} className={item2.id === profile.id ? 'active' : ''} aria-pressed={item2.id === profile.id} onClick={() => setProfile(item2)}>
                <b>{item2.id === profile.id && <Check weight="bold" />}</b>
                <strong>{isKorean ? item2.ko : item2.en}</strong>
                <small>{isKorean ? `테스트 ${item2.trials}문제 · ${item2.colors}색` : `${item2.trials} items · ${item2.colors} colors`}</small>
                <em>{isKorean ? `${item2.limit}초` : `${item2.limit} sec`}</em>
              </button>
            ))}
          </div>
          <aside className="cognition-protocol-note">
            <ShieldCheck weight="duotone" />
            <span>
              <strong>{isKorean ? '학습 기록 안내' : 'Learning record'}</strong>
              <small>{isKorean ? '이번 결과는 학습 수행 기록입니다. 각 활동의 결과는 통합 리포트에 함께 쌓입니다.' : 'This result is a learning performance record. Every activity contributes to the integrated report.'}</small>
            </span>
          </aside>
          <button type="button" className="cognition-launch-start" onClick={begin}><Play weight="fill" />{isKorean ? '학습 시작' : 'START LEARNING'}</button>
        </section>
      </main>
    );
  }

  if (report) {
    return <CognitiveReportView title={title} profileLabel={isKorean ? profile.ko : profile.en} report={report} onExit={onExit} onReplay={begin} language={language} />;
  }

  return (
    <main className="color-drum-game color-memory-game" data-localized-root>
      <CognitiveAnswerEffect result={answerEffect} language={language} />
      <header className="color-drum-game-header">
        <div className="color-drum-title">
          <span><small>{domain}</small><strong>{title} · {index + 1}/{profile.trials}</strong></span>
        </div>
        <div className="color-drum-stats">
          <span>{isKorean ? '문제' : 'ITEM'}<strong>{index + 1}/{profile.trials}</strong></span>
          <span>{isKorean ? '정답' : 'CORRECT'}<strong>{trials.filter((trial) => trial.correct).length}</strong></span>
        </div>
        <MidiStatus language={language} status={midi.status} deviceName={midi.deviceName} onScan={midi.scan} />
        <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{isKorean ? '종료' : 'EXIT'}</button>
      </header>
      <section className="color-drum-stage memory-stage">
        <div className="color-drum-instruction">
          <div>
            <span>{type === 'stroop' ? (isKorean ? '글자 색상 선택' : 'CHOOSE THE INK COLOR') : (isKorean ? '색깔 규칙 찾기' : 'FIND THE COLOR RULE')}</span>
            <h1>{subtitle}</h1>
          </div>
          <p>{(type === 'rule' ? (isKorean ? '물음표에 들어갈 다음 색깔을 찾아요' : 'Find the color that belongs at the question mark') : (isKorean ? '글자가 아닌 실제 색깔을 찾아요' : 'Choose the ink color, not the word'))} · {isKorean ? '자동 다음 문제' : 'next item starts automatically'}</p>
        </div>
        <div className="executive-card-stage">
          {type === 'stroop' ? (
            <div className="stroop-stimulus" style={{ color: (item as StroopTrial).ink.color }}>{isKorean ? (item as StroopTrial).word.ko : (item as StroopTrial).word.en}</div>
          ) : (
            <div className="rule-stimulus">
              {(item as RuleTrial).values.map((drum, i) => (
                <i key={i} style={{ background: drum.color }} aria-label={isKorean ? drum.ko : drum.en} />
              ))}
              <i className="rule-question" aria-label={isKorean ? '다음 색깔' : 'Next color'}>?</i>
            </div>
          )}
        </div>
        <div className="color-drum-feedback">{feedback || (type === 'rule' ? (isKorean ? '다음 색깔의 드럼을 눌러 보세요' : 'Play the drum for the next color') : (isKorean ? '글자의 실제 색깔과 같은 드럼을 눌러 보세요' : 'Choose the drum that matches the ink color'))}</div>
        <div className="drum-pad-grid">
          {DRUMS.map((drum) => (
            <button type="button" key={drum.id} aria-label={isKorean ? drum.ko : drum.en} className="cognitive-drum-pad" style={{ '--drum-color': drum.color } as React.CSSProperties} onPointerDown={() => choose(drum.id)}>
              <span className="drum-pad-face" />
            </button>
          ))}
        </div>
      </section>
      <footer className="color-drum-progress"><span><i style={{ width: `${(index / profile.trials) * 100}%` }} /></span><strong>{index} / {profile.trials}</strong></footer>
    </main>
  );
}

export default ExecutiveColorGame;
